import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { api, errMsg, fmt, fmtDate, FULFILL, PAY_METHOD, PAY_STATUS, STATUS } from "../../lib/api";
import { StatusBadge, btnGhost, btnPrimary, inputCls } from "../../components/ui";
import ReceiptLink from "../../components/ReceiptLink";
import { RefundDialog } from "./AdminFinanceOps";

function OrderActions({ o, reload }) {
  const [detail, setDetail] = useState(null);
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState("");
  const [refund, setRefund] = useState(null);
  useEffect(() => { api.get(`/orders/${o.id}`).then(({ data }) => setDetail(data)); }, [o.id, o.updated_at]);
  const call = async (path, body, ok) => {
    try { await api.post(`/admin/orders/${o.id}/${path}`, body); toast.success(ok); setNote(""); setChecked(false); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const canDecide = o.status !== "cancelled" && o.payment_status !== "confirmed";
  const canApprove = canDecide && (o.payment_method === "cash_on_delivery" || o.payment_status === "submitted");
  return (
    <div className="grid gap-4 border-t border-gray-100 p-4 lg:grid-cols-2" data-testid={`admin-order-actions-${o.number}`}>
      <div className="text-sm">
        <p className="font-semibold">{o.customer_name} · {o.customer_email}</p>
        <p className="text-gray-600">{o.address.phone} · {o.address.address_line}, {o.address.district}</p>
        {o.address.notes && <p className="text-gray-500">Notas: {o.address.notes}</p>}
        <ul className="mt-2">{o.items.map((i) => <li key={i.offer_id}>{i.qty} × {i.name} <span className="text-gray-500">({i.store_name}) · {fmt(i.line_total)}</span></li>)}</ul>
        <p className="mt-2 text-xs text-gray-500">Entrega {fmt(o.delivery_fee)} · {o.delivery_method === "pickup" ? "Levantamento" : "Domicílio"}</p>
        <p className="mt-1 text-xs">Preparação nas lojas: {Object.values(o.store_fulfillment).map((v) => FULFILL[v]).join(" / ")}</p>
      </div>
      <div className="flex flex-col gap-3 text-sm">
        <p className="font-semibold">Comprovativos</p>
        {detail?.receipts?.length ? detail.receipts.map((r) => <ReceiptLink key={r.id} r={r} />) : <p className="text-gray-500">Sem comprovativos.</p>}
        {canDecide && (
          <div className="flex flex-col gap-2 rounded-xl bg-bo-bg p-3">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1" data-testid="admin-received-checkbox" />
              <span>Verifiquei o recebimento efetivo do valor de <strong>{fmt(o.total)}</strong> na conta bancária.</span>
            </label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota / motivo de rejeição" className={inputCls} data-testid="admin-payment-note-input" />
            <div className="flex flex-wrap gap-2">
              <button disabled={!checked || !canApprove} onClick={() => call("payment", { approve: true, received_confirmed: true, note }, "Pagamento confirmado")} className={`${btnPrimary} px-3 py-1.5`} data-testid="admin-approve-payment-btn">Confirmar pagamento</button>
              {o.payment_status === "submitted" && <button onClick={() => call("payment", { approve: false, note }, "Comprovativo rejeitado")} className={`${btnGhost} px-3 py-1.5 text-red-600`} data-testid="admin-reject-payment-btn">Rejeitar comprovativo</button>}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {o.status === "ready" && o.delivery_method === "delivery" && <button onClick={() => call("status", { status: "out_for_delivery" }, "Saiu para entrega")} className={`${btnPrimary} px-3 py-1.5`} data-testid="admin-dispatch-btn">Saiu para entrega</button>}
          {(o.status === "out_for_delivery" || (o.status === "ready" && o.delivery_method === "pickup")) && <button onClick={() => call("status", { status: "delivered" }, "Encomenda entregue")} className={`${btnPrimary} px-3 py-1.5`} data-testid="admin-delivered-btn">Marcar entregue</button>}
          {o.payment_status === "refund_pending" && <button onClick={() => setRefund({ kind: "order", id: o.id, number: o.number, amount: o.total })} className={`${btnPrimary} px-3 py-1.5`} data-testid="admin-refunded-btn">Marcar reembolsado</button>}
          <RefundDialog key={refund?.id || "none"} target={refund} onClose={() => setRefund(null)} onDone={() => { setRefund(null); reload(); }} />
          {["pending_payment", "confirmed", "preparing", "ready"].includes(o.status) && <button onClick={() => window.confirm("Cancelar e repor stock?") && call("status", { status: "cancelled", note }, "Encomenda cancelada")} className={`${btnGhost} px-3 py-1.5`} data-testid="admin-cancel-order-btn">Cancelar encomenda</button>}
        </div>
      </div>
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => {
    const params = filter === "submitted" ? { payment_status: "submitted" } : filter ? { status: filter } : {};
    api.get("/admin/orders", { params }).then(({ data }) => setOrders(data));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="admin-orders">
      <div className="mb-3 flex flex-wrap gap-2">
        {[["", "Todas"], ["submitted", "Comprovativos por validar"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`admin-orders-filter-${k || "all"}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === k ? "bg-bo-green text-white" : "bg-white text-gray-600"}`}>{l}</button>))}
      </div>
      {!orders.length && <p className="bo-card p-8 text-center text-sm text-gray-500">Sem encomendas.</p>}
      <div className="flex flex-col gap-2">
        {orders.map((o) => (
          <div key={o.id} className="bo-card overflow-hidden" data-testid={`admin-order-${o.number}`}>
            <button onClick={() => setOpen(open === o.id ? null : o.id)} className="flex w-full flex-wrap items-center gap-3 p-4 text-left" data-testid={`admin-order-toggle-${o.number}`}>
              <span className="font-bold">{o.number}</span><StatusBadge status={o.status} />
              <span className={`text-xs ${o.payment_status === "submitted" ? "font-bold text-amber-700" : "text-gray-500"}`}>{PAY_STATUS[o.payment_status]} · {PAY_METHOD[o.payment_method]}</span>
              <span className="text-xs text-gray-400">{fmtDate(o.created_at)}</span>
              <span className="ml-auto font-bold">{fmt(o.total)}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${open === o.id ? "rotate-180" : ""}`} />
            </button>
            {open === o.id && <OrderActions o={o} reload={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
